import React, { useEffect, useState } from 'react';
import { 
    Box, Container, Grid, Typography, Button, Paper, 
    Tabs, Tab, Switch, Stack, Divider, Avatar, useTheme 
} from "@mui/material";
import { motion } from "framer-motion";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faArrowUpRightFromSquare, faCheckCircle, faUtensils, 
    faTicket, faBriefcase, faRobot, faBolt, faShieldAlt, 
    faDatabase, faHeadset, faUsersSlash, faCommentDots, 
    faPuzzlePiece, faUsers, faHeart,
    faTags
} from "@fortawesome/free-solid-svg-icons";

// Imágenes (Asegúrate de ajustar a tus rutas)
import dashboardMockup from '../../assets/images/site/forbusiness/imagen1.jpg'; 
import ContactUs2 from "./contactUs2";
import Copyright from '../../components/copyRight';

export default function ForBusiness() {
    const { t, i18n } = useTranslation();
    const history = useHistory();
    const theme = useTheme();

    const [activeVertical, setActiveVertical] = useState(0);
    const [isAnnual, setIsAnnual] = useState(true);

    const redirect = (route) => history.push(route);
    const isEs = i18n.language === 'es';

    useEffect(()=>{
        window.scrollTo(0,0);
    },[])

    // --- VARIANTES DE ANIMACIÓN (Framer Motion) ---
    const fadeUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
    };

    const cardVariant = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    // --- DATOS: LOS DOLORES ---
    const painPoints = [
        {
            icon: faTicket,
            color: '#ef4444', bgcolor: '#fef2f2',
            text: isEs ? "¿Largas filas, reventa y control manual en la puerta de tus eventos?" : "Long lines, scalping, and manual control at your event doors?"
        },
        {
            icon: faCommentDots,
            color: '#f59e0b', bgcolor: '#fffbeb',
            text: isEs ? "¿Pierdes ventas por no poder contestar mensajes a las 2:00 AM?" : "Losing sales because you can't answer messages at 2:00 AM?"
        },
        {
            icon: faPuzzlePiece,
            color: '#8b5cf6', bgcolor: '#f5f3ff',
            text: isEs ? "¿Pagas 5 herramientas distintas (y costosas) que no se conectan entre sí?" : "Paying for 5 different (and expensive) tools that don't connect?"
        },
        {
            icon: faUsersSlash,
            color: '#3b82f6', bgcolor: '#eff6ff',
            text: isEs ? "¿No sabes quiénes son tus mejores clientes ni cómo hacer que vuelvan?" : "Don't know who your best customers are or how to make them return?"
        }
    ];

    // --- DATOS: VERTICALES ---
    const verticals = [
        {
            icon: faUtensils,
            title: isEs ? "Gastronomía y Locales" : "Gastronomy & Local Shops",
            hook: isEs ? "Mesas llenas. Cero comisiones." : "Full tables. Zero commissions.",
            features: [
                isEs ? "Menú QR interactivo con up-selling automático." : "Interactive QR menu with automatic up-selling.",
                isEs ? "Reservas online automáticas 24/7." : "24/7 automated online reservations.",
                isEs ? "Cupones dinámicos para revivir los días flojos." : "Dynamic coupons to revive slow days."
            ]
        },
        {
            icon: faTicket,
            title: isEs ? "Eventos y Productoras" : "Events & Productions",
            hook: isEs ? "Control total. Cero fraude." : "Total control. Zero fraud.",
            features: [
                isEs ? "Ticketing QR dinámico (imposible de clonar)." : "Dynamic QR ticketing (impossible to clone).",
                isEs ? "Mapeo interactivo de asientos y zonas." : "Interactive seating and zone mapping.",
                isEs ? "Cobros 100% Cashless en barras y stands." : "100% Cashless payments at bars and stands."
            ]
        },
        {
            icon: faBriefcase,
            title: isEs ? "Servicios y Profesionales" : "Services & Professionals",
            hook: isEs ? "Ventas automáticas. Ingresos recurrentes." : "Automatic sales. Recurring revenue.",
            features: [
                isEs ? "Landing pages profesionales creadas en minutos." : "Professional landing pages created in minutes.",
                isEs ? "Gestión de citas y agenda sincronizada." : "Appointment management and synchronized agenda.",
                isEs ? "Cobro automatizado de suscripciones y membresías." : "Automated billing for subscriptions and memberships."
            ]
        }
    ];

    return (
        <>
            
            {/* ==========================================
                BLOQUE 1: HERO SECTION
            ========================================== */}
            <Container maxWidth="xl">
                <Grid container spacing={6} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
                            
                            <Box sx={{ mb: 3, display: 'inline-block', px: 2, py: 0.8, bgcolor: 'link.lightTransparent', color: 'link.main', borderRadius: 8, fontWeight: '800', fontSize: '0.8rem', letterSpacing: 1, border: '1px solid #e2e8f0' }}>
                                {isEs ? "NUEVO ECOSISTEMA ATE BUSINESS" : "NEW ATE BUSINESS ECOSYSTEM"}
                            </Box>
                            
                            <Typography variant='h2' component='h1' fontWeight='900' color='text.primary' sx={{ lineHeight: 1.1, mb: 3, fontSize: { xs: '2.5rem', md: '3.8rem' }, letterSpacing: '-1px' }}>
                                {isEs ? "Opera tu negocio en " : "Operate your business on "}
                                <Typography component="span" variant="inherit" color="primary.main">
                                    {isEs ? "piloto automático " : "autopilot "}
                                </Typography>
                            </Typography>
                            
                            <Typography variant='h6' color='text.secondary' sx={{ mb: 5, fontWeight: 500, maxWidth: '95%', lineHeight: 1.6 }}>
                                {isEs 
                                    ? "Vende online, controla tus eventos, fideliza a tus clientes y procesa pagos sin ceder parte de tus ganancias a terceros."
                                    : "Sell online, control your events, build loyalty, and process payments without giving up your profits to third parties."}
                            </Typography>
                            
                            {/* Trust Strip (Banda tipográfica limpia basada en la imagen) */}
                            <Box sx={{ mb: 6, width: '100%', pr: { xs: 0, md: 6 } }}>
                                <Stack 
                                    direction="row" 
                                    justifyContent="space-between" 
                                    alignItems="flex-start"
                                    divider={<Divider orientation="vertical" flexItem sx={{ mx: { xs: 1, sm: 3 }, borderColor: '#cbd5e1' }} />}
                                >
                                    <Box flex={1}>
                                        <Typography variant="h2" fontWeight="900" color="text.primary" sx={{ fontSize: { xs: '2rem', md: '3.5rem' }, letterSpacing: '-1px' }}>
                                            0%
                                        </Typography>
                                        <Typography variant="subtitle2" color="#475569" fontWeight="800" sx={{ mt: -0.5 }}>
                                            {isEs ? "Comisión por venta" : "Sales commission"}
                                        </Typography>
                                    </Box>
                                    <Box flex={1}>
                                        <Typography variant="h2" fontWeight="900" color="text.primary" sx={{ fontSize: { xs: '2rem', md: '3.5rem' }, letterSpacing: '-1px' }}>
                                            100%
                                        </Typography>
                                        <Typography variant="subtitle2" color="#475569" fontWeight="800" sx={{ mt: -0.5 }}>
                                            {isEs ? "Control de tus datos" : "Data control"}
                                        </Typography>
                                    </Box>
                                    <Box flex={1}>
                                        <Typography variant="h2" fontWeight="900" color="text.primary" sx={{ fontSize: { xs: '2rem', md: '3.5rem' }, letterSpacing: '-1px' }}>
                                            24h
                                        </Typography>
                                        <Typography variant="subtitle2" color="#475569" fontWeight="800" sx={{ mt: -0.5 }}>
                                            {isEs ? "Soporte real" : "Real support"}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>

                            {/* Botones */}
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={6}>
                                <Button 
                                    variant='contained' size="large"
                                    sx={{ borderRadius: 8, px: 4, py: 1.8, fontSize: '1.1rem', fontWeight: 'bold', textTransform: 'none', boxShadow: '0 8px 20px rgba(20, 122, 194, 0.3)' }} 
                                    onClick={() => redirect('/auth/merchantSignUp')}
                                >
                                    {isEs ? "Digitalizar mi negocio hoy" : "Digitize my business today"}
                                </Button>
                                <Button 
                                    variant='outlined' size="large"
                                    endIcon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
                                    sx={{ borderRadius: 8, px: 4, py: 1.8, fontSize: '1.1rem', fontWeight: 'bold', textTransform: 'none', borderWidth: 2, '&:hover': { borderWidth: 2 } }} 
                                    onClick={() => redirect('/merchants/services')}
                                >
                                    {isEs ? "Agendar Demo" : "Book a Demo"}
                                </Button>
                            </Stack>

                        </motion.div>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                        <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
                            <Box sx={{ position: 'relative' }}>
                                <Box sx={{ position: 'absolute', top: '5%', right: '-5%', width: '100%', height: '100%', bgcolor: 'primary.light', opacity: 0.15, borderRadius: '24px', transform: 'rotate(3deg)', zIndex: 0 }} />
                                <img 
                                    src={dashboardMockup} 
                                    alt='ATE Interface' 
                                    style={{ width: '100%', borderRadius: 16, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', position: 'relative', zIndex: 1, border: '1px solid #e2e8f0' }} 
                                />
                            </Box>
                        </motion.div>
                    </Grid>
                </Grid>
            </Container>

            {/* ==========================================
                BLOQUE 2: LOS DOLORES (Estilo Tarjetas Altas Unificadas)
            ========================================== */}
            <Box sx={{ bgcolor: '#ffffff', py: { xs: 10, md: 15 }, borderTop: '1px solid #f1f5f9' }}>
                <Container maxWidth="xl">
                    <Box textAlign="center" mb={8} maxWidth="800px" mx="auto">
                        <Typography variant="h3" fontWeight="900" color="text.primary">
                            {isEs ? "¿Hacer crecer tu negocio se siente como un caos?" : "Does growing your business feel like chaos?"}
                        </Typography>
                    </Box>

                    <Grid container spacing={3} justifyContent="center" alignItems="stretch">
                        {[
                            { 
                                title: isEs ? "Caos operativo" : "Operational chaos",
                                text: isEs ? "¿Largas filas, reventa de entradas y control manual en la puerta de tus eventos?" : "Long lines, ticket scalping, and manual control at your event doors?",
                                icon: faTicket
                            },
                            { 
                                title: isEs ? "Ventas perdidas" : "Lost sales",
                                text: isEs ? "¿Dejas clientes en visto y pierdes ventas por no poder contestar mensajes a las 2:00 AM?" : "Leaving customers on read and losing sales because you can't reply at 2:00 AM?",
                                icon: faCommentDots
                            },
                            { 
                                title: isEs ? "Gastos ocultos" : "Hidden costs",
                                text: isEs ? "¿Pagas 5 herramientas distintas (y muy costosas) que al final del día no se conectan entre sí?" : "Paying for 5 different (and very expensive) tools that don't connect with each other?",
                                icon: faPuzzlePiece
                            },
                            { 
                                title: isEs ? "Fuga de clientes" : "Customer churn",
                                text: isEs ? "¿No sabes quiénes son tus mejores compradores, qué les gusta, ni cómo hacer que vuelvan?" : "Don't know who your best buyers are, what they like, or how to make them return?",
                                icon: faUsersSlash
                            }
                        ].map((pain, idx) => (
                            <Grid item xs={12} sm={6} md={3} key={idx}>
                                <motion.div 
                                    initial={{ opacity: 0, y: 20 }} 
                                    whileInView={{ opacity: 1, y: 0 }} 
                                    viewport={{ once: true }} 
                                    transition={{ delay: idx * 0.1 }}
                                    style={{ height: '100%' }}
                                >
                                    <Paper elevation={0} sx={{ 
                                        bgcolor: '#f8fafc', // Gris muy claro y unificado
                                        border: '1px solid #e2e8f0', // Borde sutil
                                        p: { xs: 4, md: 5 }, 
                                        borderRadius: 6, 
                                        height: '100%', 
                                        display: 'flex', 
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        transition: 'transform 0.3s ease, border-color 0.3s ease',
                                        '&:hover': { transform: 'translateY(-5px)', borderColor: '#cbd5e1' }
                                    }}>
                                        <Typography variant="h6" fontWeight="900" color="text.primary" mb={2}>
                                            {pain.title}
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500, lineHeight: 1.6 }}>
                                            {pain.text}
                                        </Typography>
                                    </Paper>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ==========================================
                BLOQUE 3: LAS VENTAJAS (Estilo Promotty - Soluciones)
            ========================================== */}
            <Box sx={{ bgcolor: '#f4f7fb', py: { xs: 10, md: 15 } }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" mb={10} maxWidth="800px" mx="auto">
                        <Typography variant="h3" fontWeight="900" color="#111827" mb={3}>
                            {isEs ? "Ventajas de operar con el ecosistema ATE" : "Advantages of using the ATE ecosystem"}
                        </Typography>
                        <Typography variant="h6" color="#4b5563" fontWeight="400">
                            {isEs 
                                ? "Potencia tu negocio con herramientas de nivel empresarial. ATE te ayuda a aumentar tus ingresos y a construir relaciones duraderas con tus clientes." 
                                : "Power up your business with enterprise-grade tools. ATE helps you grow your revenue and build long-lasting relationships with your customers."}
                        </Typography>
                    </Box>

                    <Grid container spacing={8}>
                        {[
                            { 
                                icon: faTags, 
                                title: isEs ? "Promociones personalizadas" : "Custom promotions", 
                                desc: isEs ? "Crea cupones únicos para tu marca, como combos de fin de semana o descuentos especiales, motivando a tus clientes a explorar más de tu menú o servicios." : "Create unique coupons for your brand, such as weekend combos or special discounts, motivating customers to explore more." 
                            },
                            { 
                                icon: faRobot, 
                                title: isEs ? "Recompensas automáticas" : "Automated rewards", 
                                desc: isEs ? "Configura respuestas 24/7, confirmaciones de reserva y premios automáticos (como un regalo de cumpleaños) sin tener que mover un dedo." : "Set up 24/7 replies, booking confirmations, and automatic rewards (like a birthday gift) without lifting a finger." 
                            },
                            { 
                                icon: faShieldAlt, 
                                title: isEs ? "Autonomía financiera" : "Financial autonomy", 
                                desc: isEs ? "Procesa pagos de forma segura con tecnología Cashless o pasarelas online sin ceder tu margen de ganancia a plataformas de terceros." : "Process payments securely with Cashless technology or online gateways without giving up your profit margin to third parties." 
                            },
                            { 
                                icon: faUsers, 
                                title: isEs ? "Gestión de comunidad" : "Community management", 
                                desc: isEs ? "Usa nuestro sistema intuitivo para gestionar tu base de datos, ver analíticas en tiempo real y fidelizar a quienes más te compran." : "Use our intuitive system to manage your database, view real-time analytics, and build loyalty with your top buyers." 
                            }
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                                    <Box sx={{ pr: { md: 4 } }}>
                                        <Avatar sx={{ bgcolor: '#003366', color: 'white', width: 56, height: 56, mb: 3 }}>
                                            <FontAwesomeIcon icon={item.icon} size="lg" />
                                        </Avatar>
                                        <Typography variant="h5" fontWeight="700" color="#111827" mb={2}>
                                            {item.title}
                                        </Typography>
                                        <Typography variant="body1" color="#4b5563" sx={{ lineHeight: 1.6 }}>
                                            {item.desc}
                                        </Typography>
                                    </Box>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ==========================================
                BLOQUE 4: LA PROPUESTA DE VALOR (Centralización 360°)
            ========================================== */}
            <Box sx={{ bgcolor: '#f8fafc', py: { xs: 10, md: 15 }, borderTop: '1px solid #e2e8f0' }}>
                <Container maxWidth="xl">
                    
                    {/* El Gran Gancho (Basado en el Brochure) */}
                    <Grid container spacing={6} alignItems="center" mb={12}>
                        <Grid item xs={12} md={5}>
                            <Typography variant="overline" color="primary.main" fontWeight="900" letterSpacing={2} display="block" mb={1}>
                                {isEs ? "¿POR QUÉ ATE?" : "WHY ATE?"}
                            </Typography>
                            <Typography variant="h3" fontWeight="900" color="text.primary" sx={{ lineHeight: 1.1 }}>
                                {isEs ? "El fin del negocio " : "The end of the isolated "}
                                <Typography component="span" variant="inherit" color="primary.main">
                                    {isEs ? "aislado." : "business."}
                                </Typography>
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Typography variant="h6" color="text.secondary" fontWeight="500" sx={{ lineHeight: 1.6, borderLeft: '4px solid', borderColor: 'primary.main', pl: 3 }}>
                                {isEs 
                                    ? "No te vendemos un software; te integramos a un ecosistema vivo. Unirte a ATE significa conectar tu negocio directamente al sistema circulatorio de la ciudad. Es pasar de gritar en el desierto a estar en el centro comercial digital más grande de la región."
                                    : "We don't sell you a software; we integrate you into a living ecosystem. Joining ATE means connecting your business directly to the city's circulatory system. It's moving from shouting in the desert to being in the largest digital mall in the region."}
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Introducción a los 3 Pilares */}
                    <Box textAlign="center" mb={8} maxWidth="800px" mx="auto">
                        <Typography variant="h3" fontWeight="900" color="text.primary" mb={2}>
                            {isEs ? "Centralización 360°" : "360° Centralization"}
                        </Typography>
                        <Typography variant="h6" color="text.secondary" fontWeight="400">
                            {isEs 
                                ? "Reemplazamos la gestión fragmentada con una plataforma integral que maneja ingresos, usuarios y operaciones simultáneamente a través de tres pilares fundamentales." 
                                : "We replace fragmented management with a comprehensive platform that handles revenue, users, and operations simultaneously through three fundamental pillars."}
                        </Typography>
                    </Box>

                    {/* Las 3 Tarjetas de Pilares (Azul Profundo como el Brochure) */}
                    <Grid container spacing={4} justifyContent="center" alignItems="stretch">
                        {[
                            {
                                title: "ATE Business",
                                subtitle: isEs ? "(Gestión Comercial)" : "(Commercial Management)",
                                icon: faBriefcase,
                                features: [
                                    isEs ? "Catálogo Inteligente y Pedidos (Online/In-situ)." : "Smart Catalog and Orders (Online/On-site).",
                                    isEs ? "Gestión de Reservas, Reseñas y Suscripciones." : "Booking, Reviews, and Subscription Management.",
                                    isEs ? "Dashboard de Análisis de Datos y Métricas." : "Data Analytics and Metrics Dashboard."
                                ]
                            },
                            {
                                title: "ATE Events",
                                subtitle: isEs ? "(Entretenimiento)" : "(Entertainment)",
                                icon: faTicket,
                                features: [
                                    isEs ? "Boletería Digital y Control de Acceso (QR)." : "Digital Ticketing and Access Control (QR).",
                                    isEs ? "Sistema de Pagos Cashless (NFC, Offline)." : "Cashless Payment System (NFC, Offline).",
                                    isEs ? "Trazabilidad de consumo en barras y stands." : "Consumption traceability at bars and stands."
                                ]
                            },
                            {
                                title: "ATE Marketing",
                                subtitle: isEs ? "& Fidelización" : "& Loyalty",
                                icon: faUsers,
                                features: [
                                    isEs ? "Generación de Landing Pages Automáticas." : "Automatic Landing Page Generation.",
                                    isEs ? "Sistema de Fidelización de Clientes (Cupones)." : "Customer Loyalty System (Coupons).",
                                    isEs ? "Asistente de IA para ventas y atención 24/7." : "AI Assistant for 24/7 sales and support."
                                ]
                            }
                        ].map((pillar, idx) => (
                            <Grid item xs={12} md={4} key={idx}>
                                <motion.div 
                                    initial={{ opacity: 0, y: 30 }} 
                                    whileInView={{ opacity: 1, y: 0 }} 
                                    viewport={{ once: true }} 
                                    transition={{ delay: idx * 0.2 }}
                                    style={{ height: '100%' }}
                                >
                                    <Paper elevation={12} sx={{ 
                                        p: { xs: 4, md: 5 }, 
                                        borderRadius: 6, 
                                        height: '100%', 
                                        bgcolor: '#0f172a', // Azul corporativo muy oscuro
                                        backgroundImage: 'linear-gradient(to bottom right, #0f172a, #1e3a8a)', // Gradiente premium
                                        color: 'white',
                                        display: 'flex', 
                                        flexDirection: 'column',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        transition: 'transform 0.3s ease',
                                        '&:hover': { transform: 'translateY(-10px)' }
                                    }}>
                                        
                                        {/* Icono de fondo marca de agua */}
                                        <Box sx={{ position: 'absolute', top: -20, right: -20, opacity: 0.05, transform: 'rotate(-15deg)' }}>
                                            <FontAwesomeIcon icon={pillar.icon} style={{ fontSize: '150px' }} />
                                        </Box>

                                        <Box mb={4} textAlign="center" position="relative" zIndex={1}>
                                            <Typography variant="h4" fontWeight="900" mb={0.5}>
                                                {pillar.title}
                                            </Typography>
                                            <Typography variant="subtitle1" fontWeight="600" sx={{ color: '#93c5fd' }}>
                                                {pillar.subtitle}
                                            </Typography>
                                        </Box>

                                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 4 }} />

                                        <Stack spacing={3} flexGrow={1} position="relative" zIndex={1}>
                                            {pillar.features.map((feat, i) => (
                                                <Stack key={i} direction="row" spacing={2} alignItems="flex-start">
                                                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.1)', width: 28, height: 28, mt: 0.5 }}>
                                                        <Box sx={{ width: 6, height: 6, bgcolor: '#60a5fa', borderRadius: '50%' }} />
                                                    </Avatar>
                                                    <Typography variant="body1" fontWeight="500" sx={{ color: '#e2e8f0', lineHeight: 1.5 }}>
                                                        {feat}
                                                    </Typography>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    </Paper>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ==========================================
                BLOQUE 5: PRICING (Ancla)
            ========================================== */}
            <Box sx={{ py: { xs: 10, md: 15 } }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" mb={6}>
                        <Typography variant="overline" color="primary.main" fontWeight="800" letterSpacing={2}>
                            {isEs ? "INVERSIÓN CLARA" : "CLEAR INVESTMENT"}
                        </Typography>
                        <Typography variant="h3" fontWeight="900" mt={1} mb={4}>
                            {isEs ? "Elige el plan para tu negocio." : "Choose the plan for your business."}
                        </Typography>

                        {/* TOGGLE ANUAL / MENSUAL */}
                        <Paper elevation={0} sx={{ display: 'inline-flex', p: 1, borderRadius: 8, border: '1px solid #e2e8f0', bgcolor: '#f8fafc', alignItems: 'center' }}>
                            <Button 
                                disableElevation 
                                variant={!isAnnual ? "contained" : "text"} 
                                onClick={() => setIsAnnual(false)}
                                sx={{ borderRadius: 6, fontWeight: 'bold', px: 3, color: !isAnnual ? 'white' : 'text.secondary' }}
                            >
                                {isEs ? "Mensual" : "Monthly"}
                            </Button>
                            <Button 
                                disableElevation 
                                variant={isAnnual ? "contained" : "text"} 
                                onClick={() => setIsAnnual(true)}
                                sx={{ borderRadius: 6, fontWeight: 'bold', px: 3, color: isAnnual ? 'white' : 'text.secondary' }}
                            >
                                {isEs ? "Anual" : "Annually"} 
                                <Box component="span" sx={{ ml: 1, bgcolor: isAnnual ? 'rgba(255,255,255,0.2)' : '#dcfce7', color: isAnnual ? 'white' : '#16a34a', px: 1, py: 0.2, borderRadius: 4, fontSize: '0.7rem' }}>
                                    {isEs ? "Ahorras 2 meses" : "Save 2 months"}
                                </Box>
                            </Button>
                        </Paper>
                    </Box>

                    <Grid container spacing={4} justifyContent="center" alignItems="stretch">
                        
                        {/* PLAN CORE */}
                        <Grid item xs={12} md={5}>
                            <Paper elevation={0} sx={{ height: '100%', p: {xs: 4, md: 5}, borderRadius: 6, border: '2px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                <Box mb={4}>
                                    <Typography variant="h4" fontWeight="900" color="text.primary">Plan Core</Typography>
                                    <Typography variant="body1" color="text.secondary" mt={1} fontWeight="500">
                                        {isEs ? "Digitaliza tu negocio sin pagar por cada herramienta por separado." : "Digitize your business without paying for each tool separately."}
                                    </Typography>
                                </Box>

                                <Box mb={4} pb={4} borderBottom="1px solid #e2e8f0">
                                    <Typography variant="h2" fontWeight="900" color="text.primary">
                                        ${isAnnual ? '350' : '35'}
                                    </Typography>
                                    <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                                        {isAnnual ? (isEs ? "Facturado anualmente ($29.16/mes)" : "Billed annually ($29.16/mo)") : (isEs ? "Facturado mes a mes" : "Billed month to month")}
                                    </Typography>
                                </Box>

                                <Stack spacing={2.5} mb={6} flexGrow={1}>
                                    {["Acceso total al ecosistema ATE", "Gestión de Ticketing y Reservaciones", "CRM y Base de datos de clientes", "0% Comisiones por venta propia"].map((feat, i) => (
                                        <Stack key={i} direction="row" spacing={2} alignItems="center">
                                            <FontAwesomeIcon icon={faCheckCircle} color="#147ac2" />
                                            <Typography variant="body2" fontWeight="600" color="text.secondary">{feat}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>

                                <Button variant="outlined" size="large" fullWidth sx={{ borderRadius: 8, py: 1.8, fontWeight: '800', borderWidth: 2, '&:hover': { borderWidth: 2 } }} onClick={() => redirect('/auth/merchantSignUp')}>
                                    {isEs ? "Empezar con Core" : "Start with Core"}
                                </Button>
                            </Paper>
                        </Grid>

                        {/* PLAN IA PRO */}
                        <Grid item xs={12} md={5}>
                            <Paper elevation={12} sx={{ height: '100%', p: {xs: 4, md: 5}, borderRadius: 6, border: '3px solid #147ac2', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
                                <Box sx={{ position: 'absolute', top: 24, right: -35, bgcolor: '#147ac2', color: 'white', px: 5, py: 0.5, transform: 'rotate(45deg)', fontWeight: '900', fontSize: '0.7rem', letterSpacing: 1 }}>
                                    PRO
                                </Box>

                                <Box mb={4}>
                                    <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
                                        <FontAwesomeIcon icon={faRobot} color="#147ac2" size="lg" />
                                        <Typography variant="h4" fontWeight="900" color="text.primary">Plan IA</Typography>
                                    </Stack>
                                    <Typography variant="body1" color="text.secondary" mt={1} fontWeight="500">
                                        {isEs ? "Delega la atención al cliente y las ventas a la Inteligencia Artificial." : "Delegate customer service and sales to Artificial Intelligence."}
                                    </Typography>
                                </Box>

                                <Box mb={4} pb={4} borderBottom="1px solid #e2e8f0">
                                    <Typography variant="h2" fontWeight="900" color="primary.main">
                                        ${isAnnual ? '1,500' : '150'}
                                    </Typography>
                                    <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                                        {isAnnual ? (isEs ? "Facturado anualmente ($125/mes)" : "Billed annually ($125/mo)") : (isEs ? "Facturado mes a mes" : "Billed month to month")}
                                    </Typography>
                                </Box>

                                <Stack spacing={2.5} mb={6} flexGrow={1}>
                                    <Typography variant="subtitle2" fontWeight="800" color="text.primary">{isEs ? "TODO LO DE CORE, MÁS:" : "EVERYTHING IN CORE, PLUS:"}</Typography>
                                    {["Agente de Inteligencia Artificial propio", "Atención automatizada por WhatsApp", "Gestión autónoma de reservas"].map((feat, i) => (
                                        <Stack key={i} direction="row" spacing={2} alignItems="center">
                                            <FontAwesomeIcon icon={faCheckCircle} color="#16a34a" />
                                            <Typography variant="body2" fontWeight="700" color="text.primary">{feat}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>

                                <Button variant="contained" size="large" fullWidth sx={{ borderRadius: 8, py: 1.8, fontWeight: '900', boxShadow: '0 8px 20px rgba(20, 122, 194, 0.4)' }} onClick={() => redirect('/auth/merchantSignUp')}>
                                    {isEs ? "Escalar con IA" : "Scale with AI"}
                                </Button>
                            </Paper>
                        </Grid>

                    </Grid>
                </Container>
            </Box>

            {/* ==========================================
                FINAL: CTA Y CONTACTO
            ========================================== */}
            <Box sx={{ bgcolor: 'primary.main', py: 10, textAlign: 'center', color: 'white' }}>
                <Container maxWidth="md">
                    <Typography variant="h4" fontWeight="900" mb={3}>
                        {isEs ? "No le regales tu trabajo a plataformas de terceros." : "Don't give away your hard work to third-party platforms."}
                    </Typography>
                    <Typography variant="h6" fontWeight="400" mb={5} sx={{ opacity: 0.9 }}>
                        {isEs ? "Únete a los comercios que ya operan y crean comunidad con ATE." : "Join the businesses that are already operating and building community with ATE."}
                    </Typography>
                    <Button variant="contained" size="large" sx={{ bgcolor: 'white', color: 'primary.main', borderRadius: 8, px: 5, py: 2, fontSize: '1.2rem', fontWeight: '900', '&:hover': { bgcolor: '#f1f5f9' } }} onClick={() => redirect('/auth/merchantSignUp')}>
                        {isEs ? "Digitalizar mi negocio hoy" : "Digitize my business today"}
                    </Button>
                </Container>
            </Box>

            <ContactUs2
                title={t('contactUs.title2')}
                description={t('contactUs.text')}
                buttonText={t('contactUs.buttonText')}
            />
        </>
    );
}